import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import { SubmitFilingResponse } from '@/types/filings';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';

interface FilingTransmissionTilModalProps {
  filingDocumentId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: (transmissionId: number) => void;
}

export const FilingTransmissionTilModal: React.FC<FilingTransmissionTilModalProps> = ({
  filingDocumentId,
  isOpen,
  onClose,
  onSubmitSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await ApiService.request<SubmitFilingResponse>(
        EndpointKeys.SUBMIT_FILING_TIL,
        {
          method: 'POST',
          body: {
            filing_document_id: filingDocumentId
          }
        }
      );

      if (response.success && response.transmission_id) {
        onSubmitSuccess(response.transmission_id);
        onClose();
      } else {
        setError(response.error || 'TIL submission failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit filing in TIL mode');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Submit Filing to HMRC (TIL)
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Test in Live (TIL) Submission</strong>
              <br />
              This will validate that your filing contains the correct TIL class identifier 
              (<code>HMRC-PAYE-RTI-FPS-TIL</code>) before submitting to HMRC's test environment.
            </AlertDescription>
          </Alert>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="space-y-2">
                <h4 className="font-medium text-blue-900">TIL Validation</h4>
                <p className="text-sm text-blue-700">
                  The system will automatically verify that your XML contains the required 
                  <Badge variant="outline" className="mx-1">HMRC-PAYE-RTI-FPS-TIL</Badge>
                  class identifier. If not found, the submission will be rejected with a clear error message.
                </p>
              </div>
            </div>
          </div>

          <Alert variant="destructive" className="bg-orange-50 border-orange-200">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>Important:</strong> This is a test submission that will be sent to HMRC's 
              test environment. Ensure your filing data is appropriate for testing purposes.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              data-testid="submit-filing-til-button"
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isSubmitting ? 'Validating & Submitting...' : 'Submit TIL Filing'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 